import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Search,
  X,
  Percent,
  IndianRupee,
  Plus,
  User,
  Users,
  Phone,
  Hash,
  Calendar,
} from "lucide-react";
import Header from "../../../components/ui/Header";
import Sidebar from "../../../components/ui/Sidebar";
import Breadcrumb from "../../../components/ui/Breadcrumb";
import Select from "../../../components/ui/Select";
import NewDoctorModal from "../../../components/NewDoctorModal";
import API_BASE_URL from "../../../services/api";
import BrowseTestsModal from "./BrowseTestsModal";
import RefCenterModal from "./modals/RefCenterModal";
import NewAddressModal from "./modals/NewAddressModal";
import AddressSelectionModal from "./modals/AddressSelectionModal";
import SampleCollectionSection from "./SampleCollectionSection";
import axiosInstance from "../../../utils/axiosInstance";
import { useAuth } from "../../../context/AuthContext";
import {
  calculateAgeBreakdown,
  formatAgeBreakdown,
} from "../../../utils/ageCalculator";

const authHeaders = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});

/* --- Family Members Section ------------------------------------------------ */
const FamilyMembersSection = ({ familyMembers, selectedIds, onToggle }) => {
  if (familyMembers.length === 0) return null;

  return (
    <div className="mt-3 pt-3 border-t border-gray-200">
      <label className="block text-xs font-medium text-gray-500 mb-2">
        <Users className="w-3 h-3 inline mr-1" />
        Include Family Members
      </label>
      <div className="flex flex-wrap gap-2">
        {familyMembers.map((member) => {
          const isSelected = selectedIds.includes(member.id);
          return (
            <button
              key={member.id}
              type="button"
              onClick={() => onToggle(member.id)}
              className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs border transition-colors ${
                isSelected
                  ? "bg-blue-50 text-blue-700 border-blue-300"
                  : "bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100"
              }`}
            >
              <User className="w-3 h-3" />
              <span className="font-medium">
                {member.initial ? `${member.initial} ` : ""}{member.fullName}
              </span>
              {member.relationship && (
                <span className="text-gray-400 text-[10px]">({member.relationship})</span>
              )}
              {isSelected && <X className="w-3 h-3 ml-0.5 text-blue-500" />}
            </button>
          );
        })}
      </div>
      {selectedIds.length > 0 && (
        <p className="text-[10px] text-blue-600 mt-1.5">
          {selectedIds.length} family member(s) included — same tests will be ordered for them.
        </p>
      )}
    </div>
  );
};

const EditOrderPage = () => {
  const { id: orderId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed] = useState(false);
  const [patient, setPatient] = useState(null);
  const [patientAddresses, setPatientAddresses] = useState([]);
  const [familyMembers, setFamilyMembers] = useState([]);
  const [collectionCharge, setCollectionCharge] = useState(0);
  const [orderData, setOrderData] = useState(null);
  const [regenerateInvoice, setRegenerateInvoice] = useState(false);

  const [lists, setLists] = useState({
    doctors: [],
    centers: [],
    diagnosticCenters: [],
    coupons: [],
    sources: [],
    collectionCenters: [],
  });

  const [loading, setLoading] = useState({
    page: false,
    doctors: false,
    centers: false,
    diagnosticCenters: false,
    coupons: false,
    sources: false,
    addresses: false,
    search: false,
    collectionCenters: false,
  });

  const [modals, setModals] = useState({
    newDoctor: false,
    newRefCenter: false,
    browse: false,
    newAddress: false,
    addressSelect: false,
  });

  const [searchResults, setSearchResults] = useState([]);
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);

  const [formData, setFormData] = useState({
    registrationType: "",
    referredBy: "",
    refCenter: "",
    source: "",
    searchQuery: "",
    selectedItems: [],

    applyDiscount: false,
    discountType: "fixed",
    discountValue: 0,

    couponCode: "",
    couponApplied: false,
    selectedCoupon: null,

    remark: "",
    orderDate: new Date().toISOString().split("T")[0],
    selectedFamilyMemberIds: [],

    billingDiagnosticCenterId: "",

    homeCollection: false,
    officeCollection: true,

    selectedAddressId: "",
    collectionCenterId: "",
    centerSlotId: "",

    homeCollectionDate: new Date().toISOString().split("T")[0],
    slotId: "",
  });

  /* --- helpers --- */
  const setBusy = (key, val) => setLoading((p) => ({ ...p, [key]: val }));
  const openModal = (key, val) => setModals((p) => ({ ...p, [key]: val }));

  const safeJson = async (res) => {
    try {
      return await res.json();
    } catch {
      return null;
    }
  };

  const fetchJSON = useCallback(async (url, key) => {
    try {
      if (key) setBusy(key, true);
      const res = await fetch(url, { headers: authHeaders() });
      const data = await safeJson(res);
      return { ok: res.ok, data };
    } finally {
      if (key) setBusy(key, false);
    }
  }, []);

  const getDoctorName = (d) => `${d.initial || ""} ${d.name || ""}`.trim();

  /* --- totals --- */
  const baseTotal = useMemo(
    () =>
      formData.selectedItems.reduce(
        (sum, item) => sum + (Number(item.price) || 0),
        0,
      ),
    [formData.selectedItems],
  );

  const discountAmount = useMemo(() => {
    const total = baseTotal;
    let discount = 0;

    if (formData.couponApplied && formData.selectedCoupon) {
      const c = formData.selectedCoupon;
      if (c.discountType === "fixed")
        discount = Math.min(c.discountValue, total);
      else {
        discount = (total * c.discountValue) / 100;
        if (c.maxDiscount && discount > c.maxDiscount) discount = c.maxDiscount;
      }
    } else if (formData.applyDiscount && formData.discountValue > 0) {
      if (formData.discountType === "fixed")
        discount = Math.min(formData.discountValue, total);
      else discount = (total * formData.discountValue) / 100;
    }

    return Number(discount) || 0;
  }, [
    baseTotal,
    formData.applyDiscount,
    formData.discountValue,
    formData.discountType,
    formData.couponApplied,
    formData.selectedCoupon,
  ]);

  const finalTotal = Math.max(0, baseTotal - discountAmount + collectionCharge);

  /* --- change handler --- */
  const handleChange = (field, value) => {
    setFormData((prev) => {
      const next = { ...prev, [field]: value };

      if (field === "couponApplied" && value === true) {
        next.applyDiscount = false;
        next.discountValue = 0;
      }
      if (field === "applyDiscount" && value === true) {
        next.couponApplied = false;
        next.selectedCoupon = null;
        next.couponCode = "";
      }

      if (field === "homeCollection" && value === true) {
        next.officeCollection = false;
        next.collectionCenterId = "";
        next.centerSlotId = "";
      }
      if (field === "officeCollection" && value === true) {
        next.homeCollection = false;
        next.selectedAddressId = "";
      }

      if (field === "collectionCenterId") {
        next.centerSlotId = "";
      }

      if (field === "billingDiagnosticCenterId") {
        next.collectionCenterId = "";
        next.centerSlotId = "";
      }

      return next;
    });
  };

  /* --- diagnostic center list (role based) --- */
  const visibleDiagnosticCenters = useMemo(() => {
    try {
      const role = String(user?.role || "").toLowerCase();

      if (role === "superadmin") {
        return Array.isArray(lists.diagnosticCenters)
          ? lists.diagnosticCenters
          : [];
      }

      const assigned = Array.isArray(user?.assignedDiagnosticCenters)
        ? user.assignedDiagnosticCenters
        : [];

      const onlyCenters = assigned
        .map((x) => x?.diagnosticCenter)
        .filter(Boolean);

      const uniq = new Map();
      for (const dc of onlyCenters) {
        if (dc?.id != null) uniq.set(String(dc.id), dc);
      }
      return Array.from(uniq.values());
    } catch (err) {
      console.error("visibleDiagnosticCenters error:", err);
      return [];
    }
  }, [user?.role, user?.assignedDiagnosticCenters, lists.diagnosticCenters]);

  /* --- load order data + dropdowns --- */
  useEffect(() => {
    if (!orderId) return;

    (async () => {
      setBusy("page", true);

      try {
        // Fetch order edit data
        const editRes = await axiosInstance.get(
          `${API_BASE_URL}/api/orders/${orderId}/edit-data`,
          { withCredentials: true },
        );
        const d = editRes.data;
        setOrderData(d);

        // Load patient
        if (d.patientId) {
          const patientRes = await fetchJSON(
            `${API_BASE_URL}/api/patients/${d.patientId}`,
            null,
          );
          if (patientRes?.data?.success && patientRes?.data?.patient) {
            setPatient(patientRes.data.patient);
          }

          const [addrRes, familyRes] = await Promise.all([
            fetchJSON(`${API_BASE_URL}/api/address/patient/${d.patientId}`, "addresses"),
            fetchJSON(`${API_BASE_URL}/api/patients/${d.patientId}/family`, null),
          ]);
          setPatientAddresses(addrRes?.data?.addresses || []);
          setFamilyMembers(Array.isArray(familyRes?.data) ? familyRes.data : []);
        }

        // Convert items from edit-data format to selectedItems format
        const selectedItems = d.items.map((item) => ({
          id: item.id,
          memberId: item.memberId,
          testId: item.testId,
          packageId: item.packageId,
          name: item.name,
          category: item.category,
          type: item.packageId ? "package" : "test",
          uid: item.packageId ? `package-${item.packageId}` : `test-${item.testId}`,
          actualPrice: item.basePrice,
          price: item.price,
          basePrice: item.basePrice,
        }));

        // Pre-populate form
        setFormData((prev) => ({
          ...prev,
          selectedItems,
          source: d.source || "",
          referredBy: d.doctorId ? String(d.doctorId) : "",
          refCenter: d.refCenterId ? String(d.refCenterId) : "",
          billingDiagnosticCenterId: d.diagnosticCenterId ? String(d.diagnosticCenterId) : "",
          remark: d.remarks || "",
          homeCollection: d.isHomeSample || false,
          officeCollection: !d.isHomeSample,
          selectedAddressId: d.addressId ? String(d.addressId) : "",
          collectionCenterId: d.centerId ? String(d.centerId) : "",
          centerSlotId: d.centerSlotId ? String(d.centerSlotId) : "",
          slotId: d.slotId ? String(d.slotId) : "",
          applyDiscount: (d.discountAmount || 0) > 0,
          discountType: "fixed",
          discountValue: d.discountAmount || 0,
        }));

        setCollectionCharge(d.collectionCharge || 0);
      } catch (err) {
        console.error("Failed to load order:", err);
        alert("Failed to load order data");
        navigate(-1);
      } finally {
        setBusy("page", false);
      }
    })();
  }, [orderId, fetchJSON, navigate]);

  // Load dropdown lists
  useEffect(() => {
    (async () => {
      const [doc, cen, diag, srcRes] = await Promise.all([
        fetchJSON(`${API_BASE_URL}/api/doctors`, "doctors"),
        fetchJSON(`${API_BASE_URL}/api/reference-centers`, "centers"),
        fetchJSON(`${API_BASE_URL}/api/diagnostic-centers`, "diagnosticCenters"),
        fetchJSON(`${API_BASE_URL}/api/source`, "sources"),
      ]);

      setLists((p) => ({
        ...p,
        doctors: doc?.data?.doctors || doc?.data || [],
        centers: cen?.data?.centers || cen?.data?.data || cen?.data || [],
        diagnosticCenters: diag?.data?.data || diag?.data || [],
        sources: srcRes?.data?.source || srcRes?.data?.data || srcRes?.data || [],
      }));
    })();
  }, [fetchJSON]);

  useEffect(() => {
    if (!formData.officeCollection) return;
    if (lists.collectionCenters.length > 0) return;

    (async () => {
      setBusy("collectionCenters", true);
      const res = await fetchJSON(
        `${API_BASE_URL}/api/centers/admin?categoryIds=7,10`,
        "collectionCenters",
      );
      setLists((p) => ({
        ...p,
        collectionCenters: res?.data?.data || res?.data || [],
      }));
      setBusy("collectionCenters", false);
    })();
  }, [formData.officeCollection, lists.collectionCenters.length, fetchJSON]);

  /* --- search tests --- */
  const searchTests = async (query) => {
    const q = query?.trim();
    if (!q || q.length < 2) {
      setSearchResults([]);
      setShowSearchDropdown(false);
      return;
    }

    setBusy("search", true);
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/tests/search/all?search=${encodeURIComponent(q)}`,
        { headers: authHeaders() },
      );
      const json = await safeJson(res);

      if (!json?.success) {
        setSearchResults([]);
        setShowSearchDropdown(false);
        return;
      }

      const tests = Array.isArray(json?.data?.tests) ? json.data.tests : [];
      const checkups = Array.isArray(json?.data?.checkups)
        ? json.data.checkups
        : [];

      const results = [
        ...tests.map((t) => ({
          id: t.id,
          name: t.name,
          category: t.categoryName || "Tests",
          type: "test",
          uid: `test-${t.id}`,
          actualPrice: Number(t.actualPrice || 0),
          discount: Number(t.discount || 0),
          price: Number(t.finalPrice || 0),
        })),
        ...checkups.map((p) => ({
          id: p.id,
          name: p.name,
          category: p.categoryName || "Checkups",
          type: "package",
          uid: `package-${p.id}`,
          actualPrice: Number(p.actualPrice || 0),
          discount: Number(p.discount || 0),
          price: Number(p.finalPrice || 0),
        })),
      ];

      results.sort((a, b) => a.name.localeCompare(b.name));
      setSearchResults(results);
      setShowSearchDropdown(results.length > 0);
    } catch (e) {
      console.error("Search error:", e);
      setSearchResults([]);
      setShowSearchDropdown(false);
    } finally {
      setBusy("search", false);
    }
  };

  const addSelectedTest = (item) => {
    const uid = item.uid || `${item.type}-${item.id}`;
    setFormData((prev) => {
      const exists = prev.selectedItems.some(
        (t) => (t.uid || `${t.type}-${t.id}`) === uid,
      );
      if (exists) return prev;

      return {
        ...prev,
        selectedItems: [
          ...prev.selectedItems,
          { ...item, uid, type: item.type || "test", isNew: true },
        ],
        searchQuery: "",
      };
    });

    setSearchResults([]);
    setShowSearchDropdown(false);
  };

  const removeItem = (uid) => {
    setFormData((prev) => ({
      ...prev,
      selectedItems: prev.selectedItems.filter(
        (t) => (t.uid || `${t.type}-${t.id}`) !== uid,
      ),
    }));
  };

  const addTestFromBrowse = (items) => {
    const list = Array.isArray(items) ? items : [items];
    setFormData((prev) => {
      const existing = new Set(
        prev.selectedItems.map((x) => x.uid || `${x.type}-${x.id}`),
      );
      const normalized = list.map((x) => ({
        ...x,
        uid: x.uid || `${x.type}-${x.id}`,
        isNew: true,
      }));
      const newOnes = normalized.filter((x) => !existing.has(x.uid));

      if (newOnes.length === 0) {
        alert("All selected items are already added!");
        return prev;
      }

      return { ...prev, selectedItems: [...prev.selectedItems, ...newOnes] };
    });
  };

  /* --- modal handlers --- */
  const handleNewDoctorSave = (doctorData) => {
    setLists((p) => ({ ...p, doctors: [doctorData, ...p.doctors] }));
    setFormData((p) => ({ ...p, referredBy: String(doctorData.id) }));
    openModal("newDoctor", false);
  };

  const handleNewRefCenterSave = (centerData) => {
    setLists((p) => ({ ...p, centers: [centerData, ...p.centers] }));
    setFormData((p) => ({ ...p, refCenter: String(centerData.id) }));
    openModal("newRefCenter", false);
  };

  const handleNewAddressSave = async (addressData) => {
    const addrId = addressData?.id ?? addressData?.addressId ?? addressData?._id;
    if (!addrId) {
      alert("Address saved but ID missing. Please refresh.");
      openModal("newAddress", false);
      return;
    }

    setPatientAddresses((prev) => {
      const exists = prev.some((a) => String(a.id) === String(addrId));
      return exists ? prev : [...prev, { ...addressData, id: addrId }];
    });

    setFormData((p) => ({
      ...p,
      selectedAddressId: String(addrId),
      homeCollection: true,
      officeCollection: false,
    }));

    openModal("newAddress", false);

    if (patient?.id) {
      const addrRes = await fetchJSON(
        `${API_BASE_URL}/api/address/patient/${patient.id}`,
        "addresses",
      );
      setPatientAddresses(addrRes?.data?.addresses || []);
    }
  };

  const handleAddressSelect = (addressId) => {
    handleChange("selectedAddressId", addressId);
  };

  /* --- submit (update) --- */
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.selectedItems.length === 0)
      return alert("Please select at least one test/package.");

    if (!formData.billingDiagnosticCenterId) {
      return alert("Please select Diagnostic Center (Billing Venue).");
    }

    if (formData.homeCollection && !formData.selectedAddressId) {
      openModal("addressSelect", true);
      return;
    }

    try {
      setBusy("page", true);

      const payload = {
        items: formData.selectedItems.map((i) => ({
          id: i.id && !i.isNew ? i.id : null,
          testId: i.testId || (i.type === "test" ? i.id : null),
          packageId: i.packageId || (i.type === "package" ? i.id : null),
          price: parseFloat(i.price) || 0,
          memberId: i.memberId,
        })),
        discountAmount,
        collectionCharge,
        finalAmount: finalTotal,
        totalAmount: baseTotal,
        remarks: formData.remark,
        regenerateInvoice,

        source: formData.source || null,
        diagnosticCenterId: formData.billingDiagnosticCenterId || null,
        doctorId: formData.referredBy || null,
        refCenterId: formData.refCenter || null,
        isHomeSample: formData.homeCollection,
        centerId: formData.officeCollection && formData.collectionCenterId
          ? formData.collectionCenterId : null,
        centerSlotId: formData.officeCollection && formData.centerSlotId
          ? formData.centerSlotId : null,
        slotId: formData.homeCollection && formData.slotId
          ? formData.slotId : null,
        addressId: formData.homeCollection ? formData.selectedAddressId : null,
      };

      const res = await axiosInstance.put(
        `${API_BASE_URL}/api/orders/${orderId}/edit-tests`,
        payload,
        { withCredentials: true },
      );

      if (res.data?.success) {
        alert("Order updated successfully!");
        navigate(`/orders/${orderId}`);
      } else {
        alert("Error: " + (res.data?.message || "Unknown error"));
      }
    } catch (err) {
      console.error("Update error:", err);
      alert(
        err?.response?.data?.message ||
          err?.message ||
          "Network error. Please try again.",
      );
    } finally {
      setBusy("page", false);
    }
  };

  /* --- guards --- */
  if (loading.page && !patient) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto" />
          <p className="mt-3 text-gray-600 font-medium text-xs">
            Loading order details...
          </p>
        </div>
      </div>
    );
  }

  if (!patient) return null;

  const isPaid = orderData?.paymentStatus && orderData.paymentStatus !== "pending";

  /* --- UI --- */
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <Header
        onMenuToggle={() => setSidebarOpen((p) => !p)}
        isMenuOpen={sidebarOpen}
      />
      <Sidebar
        isCollapsed={sidebarCollapsed}
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />

      {/* Modals */}
      <NewDoctorModal
        isOpen={modals.newDoctor}
        onClose={() => openModal("newDoctor", false)}
        onSave={handleNewDoctorSave}
      />
      <RefCenterModal
        isOpen={modals.newRefCenter}
        onClose={() => openModal("newRefCenter", false)}
        onSave={handleNewRefCenterSave}
      />
      <BrowseTestsModal
        isOpen={modals.browse}
        onClose={() => openModal("browse", false)}
        onAddTests={addTestFromBrowse}
      />
      <NewAddressModal
        isOpen={modals.newAddress}
        onClose={() => openModal("newAddress", false)}
        onSave={handleNewAddressSave}
        patientId={patient?.id}
      />
      <AddressSelectionModal
        isOpen={modals.addressSelect}
        onClose={() => openModal("addressSelect", false)}
        addresses={patientAddresses}
        selectedAddressId={formData.selectedAddressId}
        onSelectAddress={handleAddressSelect}
        onAddNew={() => openModal("newAddress", true)}
        loading={loading.addresses}
      />

      <main
        className={`pt-16 transition-all duration-300 ${
          sidebarCollapsed ? "lg:ml-16" : "lg:ml-60"
        }`}
      >
        <div className="mx-auto px-3 sm:px-4 lg:px-6 py-4">
          <Breadcrumb />

          {/* Header Card */}
          <div className="bg-gradient-to-r from-amber-50 to-amber-100 border border-amber-200 rounded-lg mb-4 shadow-sm">
            <div className="px-4 py-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => navigate(`/orders/${orderId}`)}
                  className="w-7 h-7 bg-gradient-to-br from-amber-600 to-amber-700 text-white rounded-full flex items-center justify-center hover:from-amber-700 hover:to-amber-800 transition-all shadow-sm text-xs"
                >
                  &larr;
                </button>
                <div>
                  <h1 className="text-sm font-bold text-gray-900">
                    Modify Order #{orderData?.orderNumber || orderId}
                  </h1>
                  <p className="text-xs text-gray-600">
                    For patient: {patient.fullName}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="bg-gradient-to-r from-emerald-100 to-emerald-200 text-emerald-800 text-xs font-medium px-2 py-1 rounded-full shadow-sm">
                  <User className="w-2.5 h-2.5 inline mr-1" />
                  {patient.fullName}
                </span>
                <span className="bg-gradient-to-r from-amber-100 to-amber-200 text-amber-800 text-xs font-medium px-2 py-1 rounded-full shadow-sm">
                  <Hash className="w-2.5 h-2.5 inline mr-1" />
                  ID: {patient.id}
                </span>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Patient Summary */}
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
              <div className="bg-gray-200 px-4 py-2 border-b">
                <h2 className="text-xs font-bold text-gray-700 uppercase tracking-wider">
                  <User className="w-3 h-3 inline mr-1" />
                  Patient Details
                </h2>
              </div>
              <div className="p-4">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                  <div className="bg-gray-50 p-2 rounded">
                    <label className="block text-xs font-medium text-gray-500 mb-1">
                      Name
                    </label>
                    <div className="text-xs font-semibold text-gray-900">
                      {patient.initial} {patient.fullName}
                    </div>
                  </div>
                  <div className="bg-gray-50 p-2 rounded">
                    <label className="block text-xs font-medium text-gray-500 mb-1">
                      <Phone className="w-2.5 h-2.5 inline mr-1" />
                      Mobile
                    </label>
                    <div className="text-xs font-semibold text-gray-900">
                      {patient.contactNo}
                    </div>
                  </div>
                  <div className="bg-gray-50 p-2 rounded">
                    <label className="block text-xs font-medium text-gray-500 mb-1">
                      Gender / Age
                    </label>
                    <div className="text-xs font-semibold text-gray-900">
                      {patient.gender} &bull;{" "}
                      {patient.dob
                        ? formatAgeBreakdown(calculateAgeBreakdown(patient.dob))
                        : `${patient.age ?? "-"} yrs`}
                    </div>
                  </div>
                  <div className="bg-gray-50 p-2 rounded">
                    <label className="block text-xs font-medium text-gray-500 mb-1">
                      <Calendar className="w-2.5 h-2.5 inline mr-1" />
                      Order Date
                    </label>
                    <div className="text-xs font-semibold text-gray-900">
                      {new Date().toLocaleDateString("en-IN")}
                    </div>
                  </div>
                </div>

                <FamilyMembersSection
                  familyMembers={familyMembers}
                  selectedIds={formData.selectedFamilyMemberIds}
                  onToggle={(id) => {
                    const ids = formData.selectedFamilyMemberIds;
                    handleChange(
                      "selectedFamilyMemberIds",
                      ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id],
                    );
                  }}
                />
              </div>
            </div>

            {/* Order Information */}
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
              <div className="bg-gray-200 px-4 py-2 border-b rounded-t-lg">
                <h2 className="text-xs font-bold text-gray-700 uppercase tracking-wider">
                  Order Information
                </h2>
              </div>

              <div className="p-4">
                <div className="grid grid-cols-12 gap-4">
                  <div className="col-span-12 md:col-span-3">
                    <label className="block text-xs font-semibold text-gray-700 mb-1">
                      Source <span className="text-red-500">*</span>
                    </label>
                    <Select
                      searchable
                      required
                      placeholder="Select Source"
                      value={formData.source}
                      onChange={(val) => handleChange("source", val)}
                      options={lists.sources
                        .map((s) => ({ label: s.name, value: s.name }))
                        .sort((a, b) => a.label.localeCompare(b.label))}
                    />
                  </div>

                  <div className="col-span-12 md:col-span-3">
                    <label className="block text-xs font-semibold text-gray-700 mb-1">
                      Billing Venue <span className="text-red-500">*</span>
                    </label>
                    <Select
                      searchable
                      required
                      placeholder="Select Diagnostic Center"
                      value={formData.billingDiagnosticCenterId || ""}
                      onChange={(val) =>
                        handleChange("billingDiagnosticCenterId", val)
                      }
                      options={visibleDiagnosticCenters
                        .map((dc) => ({ label: dc.name, value: dc.id }))
                        .sort((a, b) => a.label.localeCompare(b.label))}
                    />
                    {String(user?.role || "").toLowerCase() !== "superadmin" && (
                      <p className="text-[10px] text-gray-500 mt-1">
                        Showing only assigned diagnostic centers.
                      </p>
                    )}
                  </div>

                  <div className="col-span-12 md:col-span-3">
                    <label className="block text-xs font-semibold text-gray-700 mb-1">
                      Referred By
                    </label>
                    <div className="flex gap-2 items-stretch">
                      <Select
                        searchable
                        placeholder="Select Doctor"
                        value={formData.referredBy}
                        onChange={(val) => handleChange("referredBy", val)}
                        options={lists.doctors
                          .map((d) => ({
                            label: `${d.initial || ""} ${d.name || ""}`.trim(),
                            value: d.id,
                          }))
                          .sort((a, b) => a.label.localeCompare(b.label))}
                        className="flex-1 min-w-0"
                      />
                      <button
                        type="button"
                        onClick={() => openModal("newDoctor", true)}
                        className="h-9 px-4 rounded-md border border-blue-200 bg-blue-50 text-blue-700
                        hover:bg-blue-100 transition-colors text-xs font-medium whitespace-nowrap"
                      >
                        + New
                      </button>
                    </div>
                  </div>

                  <div className="col-span-12 md:col-span-3">
                    <label className="block text-xs font-semibold text-gray-700 mb-1">
                      Reference Center
                    </label>
                    <div className="flex gap-2 items-stretch">
                      <Select
                        searchable
                        placeholder="Select Center"
                        value={formData.refCenter}
                        onChange={(val) => handleChange("refCenter", val)}
                        options={lists.centers
                          .map((c) => ({ label: c.name, value: c.id }))
                          .sort((a, b) => a.label.localeCompare(b.label))}
                        className="flex-1 min-w-0"
                      />
                      <button
                        type="button"
                        onClick={() => openModal("newRefCenter", true)}
                        className="h-9 px-4 rounded-md border border-blue-200 bg-blue-50 text-blue-700
                        hover:bg-blue-100 transition-colors text-xs font-medium whitespace-nowrap"
                      >
                        + New
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Search Tests */}
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
              <div className="bg-gray-200 px-4 py-2 border-b">
                <h2 className="text-xs font-bold text-gray-700 uppercase tracking-wider">
                  Select Tests / Packages
                </h2>
              </div>

              <div className="p-4">
                <div className="mb-4">
                  <label className="block text-xs font-semibold text-gray-700 mb-1">
                    Quick Search Tests &amp; Packages
                  </label>

                  <div className="relative">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400 pointer-events-none" />

                      <input
                        type="text"
                        value={formData.searchQuery}
                        onChange={(e) => {
                          const q = e.target.value;
                          handleChange("searchQuery", q);
                          if (q.trim().length >= 2) {
                            searchTests(q);
                          } else {
                            setSearchResults([]);
                            setShowSearchDropdown(false);
                          }
                        }}
                        onFocus={() => {
                          if (searchResults.length > 0)
                            setShowSearchDropdown(true);
                        }}
                        onBlur={() =>
                          setTimeout(() => setShowSearchDropdown(false), 200)
                        }
                        placeholder="Type at least 2 characters to search..."
                        className="w-full pl-8 pr-7 py-2 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all bg-white"
                      />

                      {formData.searchQuery && (
                        <button
                          type="button"
                          onClick={() => {
                            handleChange("searchQuery", "");
                            setSearchResults([]);
                            setShowSearchDropdown(false);
                          }}
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-0.5 rounded hover:bg-gray-100 transition-colors"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      )}

                      {loading.search && (
                        <div className="absolute right-7 top-1/2 -translate-y-1/2">
                          <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-600" />
                        </div>
                      )}
                    </div>

                    {showSearchDropdown && searchResults.length > 0 && (
                      <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded shadow-lg max-h-64 overflow-y-auto text-xs">
                        <div className="sticky top-0 bg-gray-50 px-3 py-1.5 border-b">
                          <div className="font-semibold text-gray-700">
                            {searchResults.length} result(s)
                          </div>
                        </div>

                        <div className="py-0.5">
                          {searchResults.map((item) => (
                            <div
                              key={item.uid}
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={() => addSelectedTest(item)}
                              className="px-3 py-2 hover:bg-blue-50 cursor-pointer flex justify-between items-center transition-colors border-b border-gray-100 last:border-b-0"
                            >
                              <div className="flex-1 min-w-0 pr-2">
                                <div className="font-medium text-gray-900 truncate">
                                  {item.name}
                                </div>
                                <div className="text-[10px] text-gray-500 mt-0.5">
                                  <span className="px-1 py-0.5 bg-gray-100 text-gray-700 rounded">
                                    {item.category}
                                  </span>
                                </div>
                              </div>

                              <div className="flex items-center gap-1.5 flex-shrink-0">
                                <div className="text-right">
                                  <div className="font-semibold text-green-600">
                                    &#8377;
                                    {Number(item.price).toLocaleString("en-IN")}
                                  </div>
                                </div>
                                <span
                                  className={`text-[9px] px-1 py-0.5 rounded font-medium ${
                                    item.type === "package"
                                      ? "bg-purple-100 text-purple-700"
                                      : "bg-blue-100 text-blue-700"
                                  }`}
                                >
                                  {item.type.toUpperCase()}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex items-center justify-between mb-4">
                  <button
                    type="button"
                    onClick={() => openModal("browse", true)}
                    className="text-blue-600 hover:text-blue-800 text-xs font-semibold flex items-center gap-1 bg-blue-50 hover:bg-blue-100 px-2 py-1 rounded transition-colors"
                  >
                    <Plus className="w-2.5 h-2.5" /> Browse all tests
                  </button>

                  {formData.selectedItems.length > 0 && (
                    <span className="text-xs font-medium text-gray-600">
                      {formData.selectedItems.length} item(s) selected
                    </span>
                  )}
                </div>

                {formData.selectedItems.length > 0 && (
                  <div className="border border-gray-200 rounded p-3 bg-gradient-to-r from-gray-50 to-gray-100">
                    <h3 className="text-xs font-bold text-gray-700 mb-2 flex items-center justify-between">
                      <span>Selected Items ({formData.selectedItems.length})</span>
                      <span className="text-[10px] text-gray-400 font-normal">Click price to edit</span>
                    </h3>
                    <div className="space-y-1.5 max-h-48 overflow-y-auto pr-1">
                      {formData.selectedItems.map((item) => {
                        const uid = item.uid || `${item.type}-${item.id}`;
                        return (
                          <div
                            key={uid}
                            className="flex justify-between items-center bg-white p-2 rounded border border-gray-200 text-xs shadow-sm"
                          >
                            <div className="flex items-center gap-1.5 flex-1 min-w-0">
                              <span className="px-1 py-0.5 text-[9px] font-bold rounded-full bg-gradient-to-r from-blue-100 to-blue-200 text-blue-700">
                                {item.type === "package" ? "PKG" : "TEST"}
                              </span>
                              <span className="font-medium text-gray-800 truncate">
                                {item.name}
                              </span>
                              {item.isNew && (
                                <span className="text-[8px] bg-blue-100 text-blue-600 font-bold px-1 py-0.5 rounded">
                                  NEW
                                </span>
                              )}
                              {Number(item.price) !== Number(item.basePrice || item.actualPrice) && (item.basePrice || item.actualPrice) > 0 && (
                                <span className="text-[9px] text-amber-600 bg-amber-50 px-1 py-0.5 rounded">
                                  Modified
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              {Number(item.price) !== Number(item.basePrice || item.actualPrice) && (item.basePrice || item.actualPrice) > 0 && (
                                <span className="text-[10px] text-gray-400 line-through whitespace-nowrap">
                                  &#8377;{Number(item.basePrice || item.actualPrice).toLocaleString("en-IN")}
                                </span>
                              )}
                              <input
                                type="number"
                                min="0"
                                value={item.price}
                                onChange={(e) => {
                                  const newPrice = e.target.value;
                                  setFormData((prev) => ({
                                    ...prev,
                                    selectedItems: prev.selectedItems.map((t) =>
                                      (t.uid || `${t.type}-${t.id}`) === uid
                                        ? { ...t, price: newPrice }
                                        : t,
                                    ),
                                  }));
                                }}
                                className="w-20 text-right text-xs font-bold text-green-600 border border-gray-200 rounded px-1.5 py-1 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none hover:border-gray-400 transition-colors"
                              />
                              <button
                                type="button"
                                onClick={() => removeItem(uid)}
                                className="text-red-500 hover:text-red-700 p-0.5 rounded hover:bg-red-50 transition-colors"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Pricing */}
            {formData.selectedItems.length > 0 && (
              <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
                <div className="bg-gray-200 px-4 py-2 border-b">
                  <h2 className="text-xs font-bold text-gray-700 uppercase tracking-wider">
                    Pricing &amp; Discount
                  </h2>
                </div>
                <div className="p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <input
                      type="checkbox"
                      checked={formData.applyDiscount}
                      onChange={(e) =>
                        handleChange("applyDiscount", e.target.checked)
                      }
                      className="w-3 h-3 text-blue-600 rounded border-gray-300"
                    />
                    <label className="text-xs font-semibold text-gray-700">
                      Apply Manual Discount
                    </label>
                  </div>

                  {formData.applyDiscount && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4 pl-4">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Discount Type
                        </label>
                        <select
                          value={formData.discountType}
                          onChange={(e) =>
                            handleChange("discountType", e.target.value)
                          }
                          className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-white"
                        >
                          <option value="percentage">Percentage (%)</option>
                          <option value="fixed">Fixed Amount (&#8377;)</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Discount Value
                        </label>
                        <div className="relative">
                          {formData.discountType === "percentage" ? (
                            <Percent className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400" />
                          ) : (
                            <IndianRupee className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400" />
                          )}
                          <input
                            type="number"
                            min="0"
                            max={
                              formData.discountType === "percentage"
                                ? 100
                                : baseTotal
                            }
                            value={formData.discountValue}
                            onChange={(e) =>
                              handleChange(
                                "discountValue",
                                Number(e.target.value || 0),
                              )
                            }
                            className="w-full pl-8 pr-2 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                            placeholder={
                              formData.discountType === "percentage"
                                ? "Enter %"
                                : "Enter amount"
                            }
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="pt-4 border-t border-gray-200">
                    <div className="space-y-1.5">
                      <div className="flex justify-between text-xs">
                        <span className="text-gray-600">Subtotal</span>
                        <span className="font-semibold text-gray-900">
                          &#8377;{baseTotal.toLocaleString("en-IN")}
                        </span>
                      </div>

                      {discountAmount > 0 && (
                        <div className="flex justify-between text-xs">
                          <span className="text-red-600">Discount Applied</span>
                          <span className="font-semibold text-red-600">
                            -&#8377;{discountAmount.toLocaleString("en-IN")}
                          </span>
                        </div>
                      )}

                      {collectionCharge > 0 && (
                        <div className="flex justify-between text-xs">
                          <span className="text-amber-700 flex items-center gap-1">
                            <IndianRupee className="w-3 h-3" />
                            Home Collection Charge
                          </span>
                          <span className="font-semibold text-amber-700">
                            +&#8377;{collectionCharge.toLocaleString("en-IN")}
                          </span>
                        </div>
                      )}

                      <div className="flex justify-between pt-2 border-t border-gray-300">
                        <span className="font-bold text-gray-900 text-xs">
                          Final Amount
                        </span>
                        <span className="font-bold text-green-600 text-sm">
                          &#8377;{finalTotal.toLocaleString("en-IN")}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Sample Collection */}
            <SampleCollectionSection
              formData={formData}
              onFieldChange={handleChange}
              patientAddresses={patientAddresses}
              collectionCenters={lists.collectionCenters}
              onOpenAddressSelect={() => openModal("addressSelect", true)}
              onOpenNewAddress={() => openModal("newAddress", true)}
              loading={loading.collectionCenters}
              baseTotal={baseTotal}
              onCollectionChargeChange={setCollectionCharge}
            />

            {/* Remark */}
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
              <div className="bg-gray-200 px-4 py-2 border-b">
                <h2 className="text-xs font-bold text-gray-700 uppercase tracking-wider">
                  Additional Notes
                </h2>
              </div>
              <div className="p-4">
                <textarea
                  value={formData.remark}
                  onChange={(e) => handleChange("remark", e.target.value)}
                  rows={2}
                  placeholder="Enter any special instructions or remarks..."
                  className="w-full px-3 py-2 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 resize-none bg-white"
                />
                <p className="text-[10px] text-gray-500 mt-1">
                  Visible to lab technicians and collection staff.
                </p>
              </div>
            </div>

            {/* Regenerate invoice toggle */}
            {isPaid && (
              <div className="bg-white rounded-lg border border-blue-200 shadow-sm overflow-hidden">
                <label className="flex items-start gap-3 cursor-pointer p-4">
                  <input
                    type="checkbox"
                    checked={regenerateInvoice}
                    onChange={(e) => setRegenerateInvoice(e.target.checked)}
                    className="mt-0.5 w-4 h-4 accent-blue-600"
                  />
                  <div>
                    <p className="text-xs font-semibold text-blue-800">Regenerate invoice &amp; send to patient</p>
                    <p className="text-[11px] text-blue-600 mt-0.5">
                      A new invoice will be generated with the updated amounts and sent to the patient via WhatsApp.
                    </p>
                  </div>
                </label>
              </div>
            )}

            {/* Submit */}
            <div className="flex flex-col sm:flex-row justify-between items-center gap-4 pt-4 border-t border-gray-300">
              <button
                type="button"
                onClick={() => navigate(`/orders/${orderId}`)}
                className="px-5 py-2 border border-gray-300 text-gray-700 rounded hover:bg-gray-50 transition-colors text-xs font-bold shadow-sm"
              >
                &larr; Cancel
              </button>

              <div className="flex flex-col sm:flex-row items-center gap-4">
                <div className="text-right">
                  <div className="text-xs text-gray-600">
                    Total Payable Amount
                  </div>
                  <div className="text-lg font-bold text-green-600">
                    &#8377;{finalTotal.toLocaleString("en-IN")}
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={
                    formData.selectedItems.length === 0 ||
                    loading.page ||
                    (formData.homeCollection && !formData.selectedAddressId)
                  }
                  className="px-8 py-2.5 bg-gradient-to-r from-amber-500 to-amber-600 text-white text-xs font-bold rounded hover:from-amber-600 hover:to-amber-700 disabled:opacity-50 transition-all shadow-md"
                >
                  {loading.page ? (
                    <span className="flex items-center gap-1.5">
                      <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white" />
                      Saving Changes...
                    </span>
                  ) : (
                    "Save Changes"
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
};

export default EditOrderPage;
